// @flow

import OptionManager from "./OptionManager";
import type {PhaseState} from "../components/CalculationPhaseInput";

import type {
    CalculationInputInsertionFlexibleLining,
    CalculationInputTube,
    CalculationInputTubeCracking,
    CalculationRequestProjectInput,
    CalculationInputDirectedDrilling,
    CalculationInputSliplining, CalculationInputPuLiner, CalculationInputTubeWithoutMaterialAndType
} from './RequestTypes';
import type {CalculationInputTubeWithoutMaterial} from './RequestTypes';
import type {CalculationRequestInput} from './RequestTypes';
import type {ProjectUiInput} from "../store/Calculation";

export default class RequestDataCreator {

    optionManager: OptionManager;
    
    constructor(optionManager: OptionManager) {
        this.optionManager = optionManager;
        
    }
    
    createProjectRequest(state: ProjectUiInput): CalculationRequestProjectInput {
        let phaseStates = state.phaseStates.map(phaseState => this.createRequest(phaseState));
        return {
            projectName: state.projectName,
            organization: state.organisationName,
            personName: state.personName,
            frontEndVersion: state.version,
            phases: phaseStates,
        };
    }
    
    createRequest(state: PhaseState): CalculationRequestInput {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        // Common values used in several places
        // Distances
        const distanceReceiverFillingKm = state.distanceReceiverFillingKm;
        const distanceReceiverCleanAsphaltKm = state.distanceReceiverCleanAsphaltKm;
        const distanceReceiverPollutedAsphaltKm = state.distanceReceiverPollutedAsphaltKm;
        // const distanceReceiverOldTubesKm = state.distanceReceiverOldTubesKm;
        const distanceFillingKm = state.distanceFillingKm;
        
        return {
            techniquesToCompare: opMgr.getServerStrValue(state.techniquesToCompare, sel.techniquesToCompare),
            techniquesToCompareUiCode: state.techniquesToCompare,
            basicInfoBefore: {
                typeOfLandscape: opMgr.getServerStrValue(state.typeOfLandscapeBefore, sel.typeOfLandscape),
                coating: opMgr.getServerStrValue(state.coatingBefore, sel.coating),
                streetType: opMgr.getServerStrValue(state.streetTypeBefore, sel.streetType), // Can be ""
                distanceReceiverFillingKm: distanceReceiverFillingKm,
                distanceReceiverCleanAsphaltKm: distanceReceiverCleanAsphaltKm,
                distanceReceiverPollutedAsphaltKm: distanceReceiverPollutedAsphaltKm,
                tubes: this.createRequestTubes('existingTube', state.numExistingTubes, state) 
            },
            basicInfoAfter: {
                typeOfLandscape: opMgr.getServerStrValue(state.typeOfLandscapeAfter, sel.typeOfLandscape),
                coating: opMgr.getServerStrValue(state.coatingAfter, sel.coating),
                streetType: opMgr.getServerStrValue(state.streetTypeAfter, sel.streetType), // Can be ""
                asphaltType: opMgr.getServerStrValue(state.asphaltTypeAfter, sel.asphaltType), // Can be ""
                distanceFillingKm: distanceFillingKm,
            },
            traditionalDigging: {
                shaftDepthM: state.shaftDepthM,
                slopeAngleN1: opMgr.getServerNumValue(state.slopeAngleN1, sel.slopeAngle),
                shaftCapacityM3PerHour: state.shaftCapacityM3PerHour,
                shaftLengthM: state.shaftLengthM,
                distanceReceiverOldTubesKm: state.distanceReceiverOldTubesKm,
                tubes: this.createRequestTubes('traditionalTube', state.numTraditionalTubes, state)
            },
            insertionFlexibleLining: this.createRequestInsertionFlexibleLining(state),
            directedDrilling: this.createRequestDirectedDrilling(state), 
            tubeCracking: this.createRequestTubeCracking(state),
            sliplining: this.createRequestSliplining(state),
            puLiner: this.createRequestPuLining(state),
        };
        
    }
    
    createRequestInsertionFlexibleLining(state: PhaseState): ?CalculationInputInsertionFlexibleLining {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        const numShafts = state.iflPointShafts;
        
        if (opMgr.isInsertionFlexibleLiningUsed(state.techniquesToCompare)) {
            return {
                pointShafts: numShafts,
                metersLongerBottomLengthThanWidth: numShafts > 0 ? state.iflMetersLongerBottomLengthThanWidth : 0,
                shaftDepthM: numShafts > 0 ? state.iflShaftDepthM : 0,
                slopeAngleN1: numShafts > 0 ? opMgr.getServerNumValue(state.iflSlopeAngleN1, sel.slopeAngle) : 0,
                shaftCapacityM3PerHour: numShafts > 0 ? state.iflShaftCapacityM3PerHour : 0,
                phaseLengthM: numShafts > 0 ? state.iflShaftLengthM : state.shaftLengthM, // Shaft length is necessary to get results per meter
                distanceReceiverOldTubesKm: numShafts > 0 ? state.distanceReceiverOldTubesKm : 0,
                tubes: this.createRequestTubesWithoutMaterial('iflTube', state.numIflTubes, state)
            }
        }
        else
            return null;
    }
    
    createRequestTubeCracking(state: PhaseState): ?CalculationInputTubeCracking {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        const numShafts = state.tcPointShafts;
        
        if (opMgr.isTubeCrackingUsed(state.techniquesToCompare)) {
            return {
                pointShafts: numShafts,
                metersLongerBottomLengthThanWidth: numShafts > 0 ? state.tcMetersLongerBottomLengthThanWidth : 0,
                shaftDepthM: numShafts > 0 ? state.tcShaftDepthM : 0,
                slopeAngleN1: numShafts > 0 ? opMgr.getServerNumValue(state.tcSlopeAngleN1, sel.slopeAngle) : 0,
                shaftCapacityM3PerHour: numShafts > 0 ? state.tcShaftCapacityM3PerHour : 0,
                phaseLengthM: numShafts > 0 ? state.tcShaftLengthM : state.shaftLengthM, // Shaft length is necessary to get results per meter
                distanceReceiverOldTubesKm: numShafts > 0 ? state.distanceReceiverOldTubesKm : 0,
                tubes: this.createRequestTubes('tcTube', state.numTcTubes, state)
            }
        }
        else
            return null;
    }
    
    createRequestDirectedDrilling(state: PhaseState): ?CalculationInputDirectedDrilling {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        const numShafts = state.ddPointShafts;
        
        if (opMgr.isDirectedDrillingUsed(state.techniquesToCompare)) {
            return {
                pointShafts: numShafts,
                metersLongerBottomLengthThanWidth: numShafts > 0 ? state.ddMetersLongerBottomLengthThanWidth : 0,
                shaftDepthM: numShafts > 0 ? state.ddShaftDepthM : 0,
                slopeAngleN1: numShafts > 0 ? opMgr.getServerNumValue(state.ddSlopeAngleN1, sel.slopeAngle) : 0,
                shaftCapacityM3PerHour: numShafts > 0 ? state.ddShaftCapacityM3PerHour : 0,
                phaseLengthM: numShafts > 0 ? state.ddShaftLengthM : state.shaftLengthM, // Shaft length is necessary to get results per meter
                distanceReceiverOldTubesKm: numShafts > 0 ? state.distanceReceiverOldTubesKm : 0,
                tubes: this.createRequestTubes('ddTube', state.numDdTubes, state)
            }
        }
        else
            return null;
    }

    createRequestSliplining(state: PhaseState): ?CalculationInputSliplining {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        const numShafts = state.slPointShafts;

        if (opMgr.isSlipliningUsed(state.techniquesToCompare)) {
            return {
                pointShafts: numShafts,
                metersLongerBottomLengthThanWidth: numShafts > 0 ? state.slMetersLongerBottomLengthThanWidth : 0,
                shaftDepthM: numShafts > 0 ? state.slShaftDepthM : 0,
                slopeAngleN1: numShafts > 0 ? opMgr.getServerNumValue(state.slSlopeAngleN1, sel.slopeAngle) : 0,
                shaftCapacityM3PerHour: numShafts > 0 ? state.slShaftCapacityM3PerHour : 0,
                phaseLengthM: numShafts > 0 ? state.slShaftLengthM : state.shaftLengthM, // Shaft length is necessary to get results per meter
                distanceReceiverOldTubesKm: numShafts > 0 ? state.distanceReceiverOldTubesKm : 0,
                tubes: this.createRequestTubes('slTube', state.numSlTubes, state)
            }
        }
        else
            return null;
    }

    createRequestPuLining(state: PhaseState): ?CalculationInputPuLiner {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        const numShafts = state.pulPointShafts;

        if (opMgr.isPuLiningUsed(state.techniquesToCompare)) {
            return {
                pointShafts: numShafts,
                metersLongerBottomLengthThanWidth: numShafts > 0 ? state.pulMetersLongerBottomLengthThanWidth : 0,
                shaftDepthM: numShafts > 0 ? state.pulShaftDepthM : 0,
                slopeAngleN1: numShafts > 0 ? opMgr.getServerNumValue(state.pulSlopeAngleN1, sel.slopeAngle) : 2, // For other techs 0 is sent but gives error in excel calculation for PU liner, shouldn't affect result, set to valid value
                shaftCapacityM3PerHour: numShafts > 0 ? state.pulShaftCapacityM3PerHour : 0,
                phaseLengthM: numShafts > 0 ? state.pulShaftLengthM : state.shaftLengthM, // Shaft length is necessary to get results per meter
                distanceReceiverOldTubesKm: numShafts > 0 ? state.distanceReceiverOldTubesKm : 0,
                tubes: this.createRequestTubesWithoutMaterial('pulTube', state.numPulTubes, state)
            }
        }
        else
            return null;
    }


    createRequestTubes(prefix: string, numTubes: number, state: PhaseState): CalculationInputTube[] {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let tubes: CalculationInputTube[] = [];
        for(let i = 1; i <= numTubes; i++) {
            tubes.push({
                tubeType: opMgr.getServerStrValue(state[`${prefix}${i}TubeType`], sel.tubeType),
                tubeMaterial: opMgr.getServerStrValue(state[`${prefix}${i}TubeMaterial`], sel.tubeMaterialAll),
                tubeDiameterMm: opMgr.getServerNumValue(state[`${prefix}${i}TubeDiameter`], sel.tubeDiameterAll),
                tubeLengthM: state[`${prefix}${i}TubeLength`]
            });
        }
        return tubes;
    }

    createRequestTubesWithoutMaterial(prefix: string, numTubes: number, state: PhaseState): CalculationInputTubeWithoutMaterial[] {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let tubes: CalculationInputTubeWithoutMaterial[] = [];
        for(let i = 1; i <= numTubes; i++) {
            tubes.push({
                tubeType: opMgr.getServerStrValue(state[`${prefix}${i}TubeType`], sel.tubeType),
                tubeDiameterMm: opMgr.getServerNumValue(state[`${prefix}${i}TubeDiameter`], sel.tubeDiameterAll),
                tubeLengthM: state[`${prefix}${i}TubeLength`]
            });
        }
        return tubes;
    }

    createRequestTubesWithoutMaterialAndType(prefix: string, numTubes: number, state: PhaseState): CalculationInputTubeWithoutMaterialAndType[] {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let tubes: CalculationInputTubeWithoutMaterialAndType[] = [];
        for(let i = 1; i <= numTubes; i++) {
            tubes.push({
                tubeDiameterMm: opMgr.getServerNumValue(state[`${prefix}${i}TubeDiameter`], sel.tubeDiameterAll),
                tubeLengthM: state[`${prefix}${i}TubeLength`]
            });
        }
        return tubes;
    }

}


// WEBPACK FOOTER //
// ./src/common/RequestDataCreator.js